import {
    EDGE_TYPE,
    DECISION_TYPE,
    EDGE_VALIDATION_STATE,
    GOALS,
    SEQUENCE_START,
    TIMERS,
    TIMER_FIELD_CONTACT,
    FLOW_TYPE,
    MAIN_CANVAS_ID,
    GOAL_EMAIL_CONFIRM,
} from '../editor.constants';

export const findIncomingEdges = (cellId, cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta.target === cellId);
};

export const findOutgoingEdges = (cellId, cells) => {
    if (!cells?.filter) return [];

    return cells.filter((cell) => cell.meta.source === cellId);
};

export const findIncomingCells = (cellId, cells) => {
    const incomingEdges = findIncomingEdges(cellId, cells);

    const incomingCells = [];

    incomingEdges.forEach((edge) => {
        const sourceCell = cells.find((cell) => cell.meta.id === edge.meta.source);

        if (sourceCell) incomingCells.push(sourceCell);
    });

    return incomingCells;
};

export const findLockedIncomingCells = (cellId, cells) => {
    const incomingEdges = findIncomingEdges(cellId, cells);

    const incomingLockedCells = [];

    incomingEdges.forEach((edge) => {
        const sourceCell = cells.find((cell) => {
            return cell.meta.id === edge.meta.source
                && cell.value?.methodLocked === '1';
        });

        if (sourceCell) incomingLockedCells.push(sourceCell);
    });

    return incomingLockedCells;
};

export const findOutgoingCells = (cellId, cells) => {
    const outgoingEdges = findOutgoingEdges(cellId, cells);
    const outgoingCells = [];

    outgoingEdges.forEach((edge) => {
        const targetCell = cells.find((cell) => cell.meta.id === edge.meta.target);

        if (targetCell) outgoingCells.push(targetCell);
    });

    return outgoingCells;
};

// for a given cell, find all cells that directly and indirectly precede the given cell id
// excludes "forward pathing" from preceding decision diamonds
// ie: the shortest path to the start of the series
export const getPrecedingCellIds = (cellId, cells) => {
    const incomingCellIds = findIncomingCells(cellId, cells).map((cell) => cell.meta.id);
    const cellIdsToCheck = [cellId];
    let cellIdToCheck = cellId;

    while (cellIdToCheck) {
        const incomingCells = findIncomingCells(cellIdToCheck, cells);

        incomingCells.forEach((cell) => {
            if (!cellIdsToCheck.includes(cell.meta.id)) {
                incomingCellIds.push(cell.meta.id);
                cellIdsToCheck.push(cell.meta.id);
            }
        });
        cellIdToCheck = cellIdsToCheck.shift();
    }

    return incomingCellIds;
};

// for a given cell, find all indirectly connected cells (ie, connected to a cell that connects to the given cell)
export const getAllConnectedCellIds = (cellId, cells) => {
    const connectedCellIds = [cellId];
    const cellIdsToCheck = [cellId];
    let cellIdToCheck = cellId;

    while (cellIdToCheck) {
        const incomingCells = findIncomingCells(cellIdToCheck, cells);
        const outgoingCells = findOutgoingCells(cellIdToCheck, cells);

        [...incomingCells, ...outgoingCells].forEach((cell) => {
            if (!connectedCellIds.includes(cell.meta.id)) {
                connectedCellIds.push(cell.meta.id);
                cellIdsToCheck.push(cell.meta.id);
            }
        });

        cellIdToCheck = cellIdsToCheck.shift();
    }

    return connectedCellIds;
};

export const getLastCellInSequence = (sequenceId, cells) => {
    const flowStartCell = cells.find((cell) => {
        return (cell.meta.parent === sequenceId) && (cell.type === SEQUENCE_START);
    });

    const linkedCellIds = [];
    let nextCell = findOutgoingCells(flowStartCell.meta.id, cells).pop();

    if (!nextCell) return flowStartCell;


    while (nextCell && !linkedCellIds.includes(nextCell.meta.id)) {
        linkedCellIds.push(nextCell.meta.id);
        const linkedCell = findOutgoingCells(nextCell.meta.id, cells).pop();

        if (linkedCell) nextCell = linkedCell;
    }

    return nextCell;
};

export const validateFieldTimerChronology = (incoming, outgoing) => {
    const WAIT_DELAY_CONSTANTS = {
        Minute: 60 * 1000,
        Hour: 60 * 60 * 1000,
        Day: 24 * 60 * 60 * 1000,
        WeekDay: 5 * 24 * 60 * 60 * 1000,
        Week: 7 * 24 * 60 * 60 * 1000,
        Month: 30 * 24 * 60 * 60 * 1000,
        Year: 365 * 24 * 60 * 60 * 1000,
        NoDelay: 0,
        InField: 0,
    };


    const incomingTimerDelay = incoming.waitDelayModifierType === 'Before'
        ? WAIT_DELAY_CONSTANTS[incoming.waitDelayType] * incoming.waitDelay * -1
        : WAIT_DELAY_CONSTANTS[incoming.waitDelayType] * incoming.waitDelay;

    const outgoingTimerDelay = outgoing.waitDelayModifierType === 'Before'
        ? WAIT_DELAY_CONSTANTS[outgoing.waitDelayType] * outgoing.waitDelay * -1
        : WAIT_DELAY_CONSTANTS[outgoing.waitDelayType] * outgoing.waitDelay;

    if (incomingTimerDelay === outgoingTimerDelay) {
        const incomingTime = {
            hour: Number.parseInt(incoming.startHour, 10) + (incoming.startAmPm === 'PM' ? 12 : 0),
            minute: Number.parseInt(incoming.startMinute, 10),
        };

        const outgoingTime = {
            hour: Number.parseInt(outgoing.startHour, 10) + (outgoing.startAmPm === 'PM' ? 12 : 0),
            minute: Number.parseInt(outgoing.startMinute, 10),
        };

        const incomingMinutes = incomingTime.hour * 60 + incomingTime.minute;
        const outgoingMinutes = outgoingTime.hour * 60 + outgoingTime.minute;

        if (incomingMinutes === outgoingMinutes) return EDGE_VALIDATION_STATE.TIMETRAVEL;

        if (incomingMinutes > outgoingMinutes) return EDGE_VALIDATION_STATE.TIMETRAVEL;
    }

    const hasInvalidTimeTravel = incomingTimerDelay > outgoingTimerDelay;

    if (hasInvalidTimeTravel) return EDGE_VALIDATION_STATE.TIMETRAVEL;

    return null;
};

export const isEdgeValid = (sourceCellId, targetCellId, cells) => {
    if (!targetCellId || !sourceCellId) return EDGE_VALIDATION_STATE.NONE;

    const sourceCell = cells.find((displayCell) => displayCell.id === sourceCellId);
    const targetCell = cells.find((displayCell) => displayCell.id === targetCellId);

    const cellIdsPrecedingSource = getPrecedingCellIds(sourceCellId, cells);
    const cellIdsConnectedToTarget = getAllConnectedCellIds(targetCellId, cells);
    const cellIdsConnectedToSource = getAllConnectedCellIds(sourceCellId, cells);
    const connectedCellIds = [...cellIdsConnectedToTarget, ...cellIdsConnectedToSource];

    const connectedCells = connectedCellIds.map((connectedCellId) => {
        return cells.find((cell) => cell.meta.id === connectedCellId);
    });

    // is cell attaching to itself?
    if (sourceCellId === targetCellId) {
        return EDGE_VALIDATION_STATE.INVALID;
    }

    // do the cells already have an edge between them?
    const hasSameEdge = cells.find((edge) => {
        return edge.type === EDGE_TYPE
                && ((edge.meta.source === sourceCellId && edge.meta.target === targetCellId)
                 || (edge.meta.target === sourceCellId && edge.meta.source === targetCellId));
    });

    if (hasSameEdge) return EDGE_VALIDATION_STATE.INVALID;

    // is cell attaching to a 'start'?
    if (targetCell.type === SEQUENCE_START) {
        return EDGE_VALIDATION_STATE.CONNECT_TO_START;
    }

    if (targetCell.type === FLOW_TYPE) {
        const decisionDiamondsTargetingTarget = findIncomingCells(targetCellId, cells).filter((cell) => cell.type === DECISION_TYPE);
        const decisionDiamondsTargetedBySource = findOutgoingCells(sourceCellId, cells).filter((cell) => cell.type === DECISION_TYPE);

        const hasDuplicateDecisionDiamond = decisionDiamondsTargetingTarget.some((decisionDiamond) => {
            return decisionDiamondsTargetedBySource.includes(decisionDiamond);
        });

        if (hasDuplicateDecisionDiamond) return EDGE_VALIDATION_STATE.REDUNDANT_DECISION_DIAMOND;
    }

    if (targetCell.type === GOAL_EMAIL_CONFIRM) {
        // This should behave as a locked sequence
        return EDGE_VALIDATION_STATE.INVALID;
    }

    // if we're in a sequence, does the source element already have an outgoing edge?
    if (sourceCell.meta.parent !== MAIN_CANVAS_ID) {
        if (findOutgoingCells(sourceCellId, cells).length) {
            return EDGE_VALIDATION_STATE.SINGLE_OUTGOING_EDGE;
        }
    }

    if (targetCell.type === DECISION_TYPE && findIncomingEdges(targetCellId, cells).length) {
        return EDGE_VALIDATION_STATE.ELEMENT_LINKED_TO_ONE_DD;
    }

    if (cells.some((cellObj) => cellIdsConnectedToSource.includes(cellObj.id) && cellObj.type === DECISION_TYPE) && targetCell.type === DECISION_TYPE) {
        return EDGE_VALIDATION_STATE.ELEMENT_LINKED_TO_ONE_DD;
    }

    // is a 'when' being connected to another 'when'?
    const allWhens = Object.values(GOALS);

    const sourceIsWhenTrigger = allWhens.includes(sourceCell.type);
    const targetIsWhenTrigger = allWhens.includes(targetCell.type);

    if (sourceIsWhenTrigger && targetIsWhenTrigger) {
        return EDGE_VALIDATION_STATE.INVALID;
    }

    // is a decision diamong being connected to a 'when'?
    if (sourceCell.type === DECISION_TYPE && targetIsWhenTrigger) {
        return EDGE_VALIDATION_STATE.DECISION_TO_GOAL;
    }

    // is the sequence feeding back into itself?
    if (cellIdsPrecedingSource.includes(targetCellId)) {
        return EDGE_VALIDATION_STATE.INFINITE_LOOP;
    }

    // does the resulting sequence have more than 1 start?
    const startCells = connectedCells.filter((cell) => cell.type === SEQUENCE_START);

    if (startCells.length >= 2) return EDGE_VALIDATION_STATE.NO_MULTIPLE_STARTS;


    // does the resulting sequence contain more than 1 type of timer? (ie: a field timer AND a date timer)
    const allTimers = Object.values(TIMERS);
    const timerElementsByType = {};

    connectedCells.forEach((cell) => {
        if (allTimers.includes(cell.type)) {
            timerElementsByType[cell.type] = timerElementsByType[cell.type] || [];
            timerElementsByType[cell.type].push(cell);
        }
    });

    if (Object.keys(timerElementsByType).length > 1) return EDGE_VALIDATION_STATE.MISMATCHED_TIMERS;

    if (timerElementsByType[TIMER_FIELD_CONTACT]) {
        const contactFieldTimers = timerElementsByType[TIMER_FIELD_CONTACT];
        const contactFieldTimerUniqueFields = [];

        contactFieldTimers.forEach((timer) => {
            if (timer.value.ready !== '1') return;

            if (contactFieldTimerUniqueFields.includes(timer.value.contactField)) return;

            contactFieldTimerUniqueFields.push(timer.value.contactField);
        });

        if (contactFieldTimerUniqueFields.length > 1) return EDGE_VALIDATION_STATE.MISMATCHED_FIELD_TIMER_FIELD;
    }

    const incomingTimer = [sourceCell, ...findIncomingCells(sourceCellId, cells)].find((cell) => {
        return cell.value.ready === '1' && allTimers.includes(cell.type);
    });
    const outgoingTimer = [targetCell, ...findOutgoingCells(targetCellId, cells)].find((cell) => {
        return cell.value.ready === '1' && allTimers.includes(cell.type);
    });

    if (incomingTimer && outgoingTimer) {
        const outgoing = outgoingTimer.value;
        const incoming = incomingTimer.value;

        if (incomingTimer.type === TIMERS.TIMER_FIELD_CONTACT && outgoingTimer.type === TIMERS.TIMER_FIELD_CONTACT) {
            const invalidTimeTravelState = validateFieldTimerChronology(incoming, outgoing);

            if (invalidTimeTravelState) return invalidTimeTravelState;
        } else if (incomingTimer.type === TIMERS.TIMER_DATE && outgoingTimer.type === TIMERS.TIMER_DATE) {
            const incomingTimerDate = new Date(`${incoming.startYear}-${incoming.startMonth}-${incoming.startDay} ${incoming.startHour}:${incoming.startMinute} ${incoming.startAmPm}`);
            const outgoingTimerDate = new Date(`${outgoing.startYear}-${outgoing.startMonth}-${outgoing.startDay} ${outgoing.startHour}:${outgoing.startMinute} ${outgoing.startAmPm}`);

            const hasInvalidTimeTravel = incomingTimerDate > outgoingTimerDate;

            if (hasInvalidTimeTravel) return EDGE_VALIDATION_STATE.TIMETRAVEL;
        }
    }

    return EDGE_VALIDATION_STATE.VALID;
};
